'use client';

import { useEffect } from 'react';
import { track } from '@/lib/analytics/events';

export default function SectionViews({ selector = 'section[id]' }: { selector?: string }) {
  useEffect(() => {
    const seen = new Set<string>();
    const sections = Array.from(document.querySelectorAll<HTMLElement>(selector));

    const isGranted = () => {
      try {
        return !!(window as any).__analyticsGranted;
      } catch {
        return false;
      }
    };

    const sendFor = (el: HTMLElement) => {
      const id = el.id || el.dataset.sectionId;
      if (!id || seen.has(id) || !isGranted()) return;
      seen.add(id);
      track.sectionView({
        section_id: id,
        section_title: el.dataset.sectionTitle || el.getAttribute('aria-label') || undefined,
        section_index: sections.indexOf(el),
        visible_ratio: 50,
      });
    };

    // Observe for subsequent views
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
            sendFor(entry.target as HTMLElement);
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: [0.5] },
    );
    sections.forEach((el) => {
      io.observe(el);
    });

    // Eager initial pass for any already-visible sections
    const eagerCheck = () => {
      if (!isGranted()) return;
      const vh = window.innerHeight || document.documentElement.clientHeight;
      sections.forEach((el) => {
        const rect = el.getBoundingClientRect();
        const elH = Math.max(1, rect.height);
        const visible = Math.min(rect.bottom, vh) - Math.max(rect.top, 0);
        if (visible / elH >= 0.5) sendFor(el);
      });
    };
    requestAnimationFrame(eagerCheck);

    // Re-check once immediately after consent is granted
    const onGranted = () => requestAnimationFrame(eagerCheck);
    window.addEventListener('bs:analytics-granted', onGranted);

    return () => {
      window.removeEventListener('bs:analytics-granted', onGranted);
      io.disconnect();
    };
  }, [selector]);

  return null;
}
